import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ListParamUpdates = Record<string, string | null>;

// Replace, not push: filter tweaks shouldn't pile up history, so Back from a view lands on the list.
export function useUpdateListParams() {
    const [searchParams, setSearchParams] = useSearchParams();
    // One call per change: setSearchParams reads a render-time snapshot, so two calls in a tick clobber.
    const updateParams = useCallback(
        (updates: ListParamUpdates) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    Object.entries(updates).forEach(([key, value]) => {
                        if (value === null) next.delete(key);
                        else next.set(key, value);
                    });
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );
    return { searchParams, updateParams };
}

// Any change to what's listed restarts at page 1, as useDataTable does.
export function usePageForSignature(signature: string) {
    const [state, setState] = useState({ signature, page: 1 });
    const page = state.signature === signature ? state.page : 1;
    const setPage = useCallback((next: number) => setState({ signature, page: next }), [signature]);
    return { page, setPage };
}

export type ListSortOrder = 'asc' | 'desc';

// Clicking the current column flips it; a new column starts at its own most useful order.
export function nextSortOrder<K extends string>(
    by: K,
    current: { sort: K; order: ListSortOrder },
    firstOrder: Record<K, ListSortOrder>
): ListSortOrder {
    if (by !== current.sort) return firstOrder[by];
    return current.order === 'asc' ? 'desc' : 'asc';
}
